import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyEliteMindDashboard } from './companyEliteMindApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import { REPORT_RAMP, CONSISTENCY_RAMP, STATUS_RAMP } from './eliteMindTheme';

/**
 * Elite Mind → Dashboard.
 *
 * ── WHAT THIS PAGE WILL NOT TELL YOU ────────────────────────────────────────
 * Every Elite Mind report carries a switch the athlete sets themselves:
 * share this with my team, or don't. It defaults to off. Every report figure on
 * this page counts only the ones they chose to share.
 *
 * So there is no "logged versus shared" number here, and there never will be.
 * It is the obvious next metric and it is precisely the harm — a club told its
 * squad logged forty injuries and shared three will go and ask why, which is
 * the pressure the switch exists to remove.
 *
 * The athlete journal has no such switch, which is itself the answer: the app
 * gives an explicit control on reports and none on the journal. So the journal
 * contributes only the fact that it is being kept and how regularly. Not the
 * symptoms, not the sleep, not the muscle tension, not the notes — and not
 * whether they trained as planned, which is the one a coach has a real
 * professional interest in and still lives in a log nobody told the athlete
 * their club could read.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * What remains is the squad, how well the journal habit is holding, and what
 * athletes have actively chosen to put in front of the club — with concussion
 * broken out on its own throughout, because it is the one thing here a club has
 * a duty to act on rather than merely observe.
 *
 * `fetcher` is a prop so a super-admin Elite Mind hub can render this page
 * unscoped; the builders behind both are the same.
 */
const EliteMindDashboard = ({
  fetcher = fetchCompanyEliteMindDashboard,
  populationLabel = 'athletes'
}) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const bySport = stats?.bySport || [];
  const consistencySplit = stats?.consistencySplit || [];
  const byReportType = stats?.byReportType || [];
  const statusMix = stats?.statusMix || [];
  const weekdayEntries = stats?.weekdayEntries || [];
  const journalTrend = stats?.journalTrend || [];
  const athletesTrend = stats?.athletesTrend || [];
  const reportsTrend = stats?.reportsTrend || [];

  // What share of the population keeps the journal.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.journalAthletes / stats.totalUsers) * 100) : null;

  const trendHasData = journalTrend.some((val) => val > 0) || reportsTrend.some((val) => val > 0);

  // The report mix is a share of shared reports, so it reads as proportions of
  // what athletes chose to show — not of everything they logged.
  const reportMix = byReportType.map((row) => ({ label: row.label, count: row.reports }));

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line', stacked: false },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal, CHART_COLORS.orange],
    stroke: { width: [0, 3, 3], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(journalTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.teal],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekdayEntries.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} entries` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="Athletes Registered"
          value={stats ? formatBigNumber(stats.athletesRegistered) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title="Keeping a Journal"
          value={stats ? formatBigNumber(stats.journalAthletes) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Journal Entries"
          value={stats ? formatBigNumber(stats.journalEntries) : '-'}
        />
        {/* Shared, and only shared. */}
        <StatCard
          icon={STAT_ICONS.completions}
          title="Reports Shared"
          value={stats ? formatBigNumber(stats.sharedReports) : '-'}
        />
        {/* The duty-of-care one. Broken out because a club must act on it. */}
        <StatCard
          icon={STAT_ICONS.more}
          title="Concussions (30 days)"
          value={stats ? formatBigNumber(stats.recentConcussions) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Entries per Athlete"
          value={stats ? stats.avgEntriesPerAthlete : '-'}
        />
      </div>

      {/* ---------- Journal habit and what gets shared ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {/* Days kept out of the last thirty. An average would let one diligent
            athlete carry the whole squad. */}
        <MiniDonutCard
          title="Journal Consistency"
          subtitle="Days kept in the last 30"
          data={consistencySplit}
          colors={CONSISTENCY_RAMP}
          unit="athletes"
          totalLabel="Athletes"
          legendLimit={4}
        />
        <MiniDonutCard
          title="What Gets Shared"
          subtitle="Reports athletes chose to show the club"
          data={reportMix}
          colors={REPORT_RAMP}
          unit="reports"
          totalLabel="Shared"
          legendLimit={3}
        />
        <MiniDonutCard
          title="Where They Were"
          subtitle="Status on a shared report"
          data={statusMix}
          colors={STATUS_RAMP}
          unit="reports"
          totalLabel="Reports"
          legendLimit={3}
        />
      </div>

      {/* ---------- Trend and shared reports ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel
          title="Activity Over Time"
          subtitle="Journal entries and shared reports — last 30 days"
        >
          {trendHasData ? (
            <Chart
              type="line"
              height={380}
              width="100%"
              series={[
                { name: 'Journal entries', type: 'column', data: journalTrend },
                { name: 'Athletes', type: 'line', data: athletesTrend },
                { name: 'Shared reports', type: 'line', data: reportsTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No journal entries or shared reports in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        {/* Fixed rows, zero-filled: "no concussions shared" has to be a visible
            row rather than an absent one. */}
        <Panel
          title="Shared Reports"
          subtitle="Only reports the athlete chose to share with the club"
        >
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Type</th>
                <th className="py-2 px-4 font-medium text-center">Shared</th>
                <th className="py-2 px-4 font-medium text-center">Athletes</th>
                <th className="py-2 px-4 font-medium text-center">Last 30 Days</th>
                <th className="py-2 font-medium text-right">Most Recent</th>
              </>
            }
          >
            {byReportType.map((row) => (
              <tr key={row.type} className={PANEL_ROW_CLASS}>
                <td className="py-2 pr-4">{row.label}</td>
                <td className="py-2 px-4 text-center">{formatBigNumber(row.reports)}</td>
                <td className="py-2 px-4 text-center">{formatBigNumber(row.athletes)}</td>
                <td className="py-2 px-4 text-center">{formatBigNumber(row.recent)}</td>
                <td className="py-2 text-right">{row.lastDate || '-'}</td>
              </tr>
            ))}
          </PanelTable>
          <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            Reports an athlete kept private are not counted here, and no total including them is
            available anywhere in this section.
          </p>
        </Panel>
      </div>

      {/* ---------- Squad and supporting detail ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="Journal Take-up"
          subtitle={`Share of ${populationLabel} keeping a journal`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.journalAthletes)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${populationLabel}`
              : null
          }
        />

        {/* Sport and position are roster facts a club already holds — the only
            athlete-supplied field in this section that is not health data. */}
        <Panel title="Sports Represented" subtitle="Athletes by sport">
          {bySport.length > 0 ? (
            <ProgressList
              items={bySport.map((row) => ({ label: row.label, count: row.athletes }))}
              unit="athletes"
              color={CHART_COLORS.brand}
            />
          ) : (
            <PanelEmpty>No athlete has set up a sport profile yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Journalling by Weekday" subtitle="When athletes write it up">
          {weekdayEntries.some((day) => day.count > 0) ? (
            <Chart
              type="bar"
              height={280}
              width="100%"
              series={[{ name: 'Entries', data: weekdayEntries.map((day) => day.count) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No journal entries yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Summary ---------- */}
      <div className="mt-6">
        <Panel title="Elite Mind Summary" subtitle="The raw counts behind this page">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Athletes who have shared a report</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.reportingAthletes) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Athletes with a concussion shared (30 days)</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.recentConcussionAthletes) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First journal entry</td>
              <td className="py-2 text-right">{stats?.firstJournalDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last journal entry</td>
              <td className="py-2 text-right">{stats?.lastJournalDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Most recent shared report</td>
              <td className="py-2 text-right">{stats?.lastReportDate || '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>
    </div>
  );
};

EliteMindDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default EliteMindDashboard;
